/**
 * Distance helpers for the arrival check.
 *
 * The server's geofence is the only thing that decides whether a completion is
 * accepted (PostGIS, against the facility pin). This is a client-side estimate
 * of the same measurement, used purely to warn a driver BEFORE they spend two
 * minutes capturing a photo and a signature that the server will refuse. When
 * the two disagree, the server is right.
 */

const EARTH_RADIUS_M = 6_371_000;
const M_TO_FT = 3.28084;

export function metersBetween(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

export const toFeet = (meters: number): number => meters * M_TO_FT;

/**
 * Directions link. `?api=1` is the documented cross-platform form: it opens the
 * Google Maps app on Android and iOS when installed and falls back to the web
 * map otherwise, so it works on every phone in the fleet without sniffing.
 *
 * Navigation targets the stored pin, not the address text, so the driver is
 * routed to the exact point the geofence measures from.
 */
export function directionsUrl(facility: {
  address: string; lat: number | null; lng: number | null;
}): string {
  const dest = facility.lat !== null && facility.lng !== null
    ? `${facility.lat},${facility.lng}`
    : facility.address;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}
